// @flow
import idx from 'idx';
import gql from 'graphql-tag';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import { FIRST_PAGE_VAR } from '../components/ChatStreamContainer';
import GET_CHAT_STREAM_ITEMS_QUERY from 'chat/components/ChatStream/queries/GetChatStreamItems.gql';
import STREAM_SECTION_CHATS_QUERY from 'chat/components/StreamSectionList/queries/StreamSectionChatsQuery.gql';
import { addSequenceItemsToQueryResult, removeSequenceItemsFromQueryResult } from './query';
import { dataIdFromObject } from 'lib/apollo/cache/memory';

export function lastStreamItemSequenceNumber(streamUUID: *): string {
  let result;

  try {
    result = getApolloClientSingleton().readQuery({
      query: GET_CHAT_STREAM_ITEMS_QUERY,
      variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
    });
  } catch (e) {
    return '0';
  }

  return idx(result, _ => _.chatStreams[0].sequenceItems[0].seq) || '0';
}

export function updateChatStreamPreview(uuid: string, date: Date, preview: string) {
  getApolloClientSingleton().writeFragment({
    id: dataIdFromObject({ __typename: 'ChatStream', uuid }),
    fragment: gql`
      fragment chatStreamPreview on ChatStream {
        updatedAt
        preview {
          preview
          sentAt
        }
      }
    `,
    variables: { uuid },
    data: {
      updatedAt: date.toISOString(),
      preview: {
        preview,
        sentAt: date.toISOString(),
        __typename: 'StreamPreview',
      },
      __typename: 'ChatStream',
    },
  });
}

// Broadcast a stream sequence item into all the
// stream that are connected to a specific group,
// this is useful for annoucements where message
// are written into all the chats.
export function broadcastStreamSequenceItem(
  groupUUID: string,
  groupId: number,
  item: *,
  options?: { overrideSequenceNumber?: boolean } = {}
) {
  let chatStreamsByGroupId;

  // first, check to see if chatStreams have been
  // loaded by groupId
  try {
    chatStreamsByGroupId = getApolloClientSingleton().readQuery({
      query: STREAM_SECTION_CHATS_QUERY,
      variables: { groupId },
    });
  } catch (e) {
    console.log('chatStreams have not yet been loaded by group ID');
  }

  if (chatStreamsByGroupId) {
    // if chatStreams have been loaded by groupId,
    // update the Apollo store so that the chat list
    // updates with the new previews
    chatStreamsByGroupId.chatStreams.forEach(({ uuid, type }) => {
      // don't broadcast into group convos
      if (type === 'group') {
        return;
      }
      updateChatStreamPreview(uuid, new Date(), item.item.preview);
    });
  }

  let cachedChatStreamsByUuid;

  // then, check to see if individual chatStreams
  // have been cached by stream uuid
  try {
    /* eslint-disable graphql/template-strings */
    cachedChatStreamsByUuid = getApolloClientSingleton().readQuery({
      query: gql`
        query GetCachedChatStream {
          cachedChatStreams {
            uuid
          }
        }
      `,
    });
    /* eslint-enable graphql/template-strings */
  } catch (e) {
    // ignore cache update if we haven't loaded any stream yet.
    return;
  }

  // if stream has been loaded, check to see if it
  // is in the group
  cachedChatStreamsByUuid.cachedChatStreams.forEach(({ uuid }) => {
    const r2 = getApolloClientSingleton().readQuery({
      query: gql`
        query GetStreamMessagesGroups($uuid: String!) {
          chatStreams(chatUuids: [$uuid]) {
            uuid
            type
            ... on ChatStream {
              groups {
                uuid
              }
            }
          }
        }
      `,
      variables: { uuid },
    });

    // if it is in the group, add the new message
    // to the stream
    r2.chatStreams.forEach(stream => {
      // don't broadcast into group convos
      if (stream.type === 'group') {
        return;
      }

      const uuids = stream.groups.map(g => g.uuid);

      if (uuids.includes(groupUUID)) {
        addStreamSequenceItem(
          stream.uuid,
          {
            ...item,
            item: {
              ...item.item,
              chat: {
                uuid: stream.uuid,
                __typename: 'ChatStream',
              },
            },
          },
          options
        );
      }
    });
  });
}

export function addStreamSequenceItem(
  streamUUID: *,
  item: *,
  options?: { overrideSequenceNumber?: boolean } = {}
) {
  let result;

  try {
    result = getApolloClientSingleton().readQuery({
      query: GET_CHAT_STREAM_ITEMS_QUERY,
      variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
    });
  } catch (e) {
    // ignore cache update for stream that are not loaded yet.
    return;
  }

  // we don't pass in the seq from putMessage because the number
  // that api returns in the payload is incorrect, so we manually
  // calculate it here
  const message = options.overrideSequenceNumber
    ? {
        ...item,
        seq: (parseInt(lastStreamItemSequenceNumber(streamUUID), 10) + 1).toString(),
      }
    : item;

  getApolloClientSingleton().writeQuery({
    query: GET_CHAT_STREAM_ITEMS_QUERY,
    variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
    data: addSequenceItemsToQueryResult(result, [message]),
  });

  updateChatStreamPreview(streamUUID, new Date(), item.item.preview);
}

export function removeStreamSequenceItem(streamUUID: *, itemUUID: string) {
  let result;

  try {
    result = getApolloClientSingleton().readQuery({
      query: GET_CHAT_STREAM_ITEMS_QUERY,
      variables: {
        uuid: streamUUID,
        ...FIRST_PAGE_VAR,
      },
    });
  } catch (e) {
    // ignore cache update for stream that are not loaded yet.
    return;
  }

  getApolloClientSingleton().writeQuery({
    query: GET_CHAT_STREAM_ITEMS_QUERY,
    variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
    data: removeSequenceItemsFromQueryResult(result, [itemUUID]),
  });
}
